<template>
  <div
    class="skeleton"
    :class="[variantClass, sizeClass, { 'skeleton-rounded': rounded }]"
    :style="customStyle"
  >
    <span class="skeleton-shimmer"></span>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'text',
    validator: (val) => ['text', 'title', 'avatar', 'button', 'card', 'custom'].includes(val)
  },
  width: {
    type: [String, Number],
    default: null
  },
  height: {
    type: [String, Number],
    default: null
  },
  rounded: {
    type: Boolean,
    default: false
  },
  lines: {
    type: Number,
    default: 1
  }
});

const variantClass = computed(() => {
  const variants = {
    text: 'skeleton-text',
    title: 'skeleton-title',
    avatar: 'skeleton-avatar',
    button: 'skeleton-button',
    card: 'skeleton-card',
    custom: ''
  };
  return variants[props.variant] || '';
});

const sizeClass = computed(() => {
  if (props.variant === 'custom') return '';
  return '';
});

const customStyle = computed(() => {
  const style = {};
  if (props.width) {
    style.width = typeof props.width === 'number' ? `${props.width}px` : props.width;
  }
  if (props.height) {
    style.height = typeof props.height === 'number' ? `${props.height}px` : props.height;
  }
  return style;
});
</script>

<style scoped>
.skeleton {
  position: relative;
  overflow: hidden;
  background-color: var(--bg-card);
  border-radius: var(--radius-sm);
}

.skeleton-shimmer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0,
    rgba(255, 255, 255, 0.05) 20%,
    rgba(255, 255, 255, 0.1) 60%,
    rgba(255, 255, 255, 0)
  );
  animation: shimmer 2s infinite;
}

/* Variants */
.skeleton-text {
  height: 1rem;
  margin-bottom: 0.5rem;
  width: 100%;
}

.skeleton-text:last-child {
  width: 80%;
}

.skeleton-title {
  height: 2rem;
  margin-bottom: 1rem;
  width: 60%;
}

.skeleton-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
}

.skeleton-button {
  height: 40px;
  width: 120px;
  border-radius: var(--radius-md);
}

.skeleton-card {
  height: 200px;
  width: 100%;
  border-radius: var(--radius-lg);
}

.skeleton-rounded {
  border-radius: var(--radius-full);
}

/* Animation */
@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .skeleton-title {
    width: 80%;
  }
  
  .skeleton-button {
    width: 100px;
  }
}
</style>